import * as React from "react";

import { cn } from "@/lib/utils";

const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<"textarea">
>(({ className, ...props }, ref) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const scrollParentRef = React.useRef<HTMLElement | null>(null);

  React.useImperativeHandle(
    ref,
    () => textareaRef.current as HTMLTextAreaElement
  );

  const adjustHeight = React.useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Find and cache scrollable parent on first call
    if (scrollParentRef.current === null) {
      let scrollParent = textarea.parentElement;
      while (scrollParent) {
        const { overflowY } = window.getComputedStyle(scrollParent);
        if (overflowY === "scroll" || overflowY === "auto") break;
        scrollParent = scrollParent.parentElement;
      }
      scrollParentRef.current = scrollParent;
    }

    const scrollTop = scrollParentRef.current?.scrollTop ?? 0;

    // Adjust height
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;

    // Restore scroll
    if (scrollParentRef.current) scrollParentRef.current.scrollTop = scrollTop;
  }, []);

  React.useEffect(() => {
    adjustHeight();
  }, [props.value, adjustHeight]);

  return (
    <textarea
      className={cn(
        "flex min-h-[80px] w-full rounded-md border border-input bg-background px-1 py-1 text-lg ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 overflow-hidden",
        className
      )}
      ref={textareaRef}
      {...props}
    />
  );
});
Textarea.displayName = "Textarea";

export { Textarea };
